
import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface InfiniteScrollTableProps {
  children: React.ReactNode;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  className?: string;
  stickyHeader?: React.ReactNode;
}

export const InfiniteScrollTable = ({ 
  children, 
  hasMore, 
  isLoading, 
  onLoadMore,
  className = "",
  stickyHeader
}: InfiniteScrollTableProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '20px',
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <div className={`border rounded-lg overflow-hidden h-full flex flex-col max-w-full ${className}`}>
      {stickyHeader && (
        <div className="sticky top-0 z-10 bg-background border-b">
          {stickyHeader}
        </div>
      )}
      <div 
        ref={scrollRef} 
        className="flex-1 overflow-y-auto"
      >
        {children}
        
        {/* Loading indicator */}
        {(isLoading || hasMore) && (
          <div 
            ref={loadingRef}
            className="flex items-center justify-center p-4 border-t bg-background"
          >
            {isLoading && (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Loading more...</span>
              </>
            )}
            {!isLoading && hasMore && (
              <span className="text-sm text-muted-foreground">Scroll to load more</span>
            )}
          </div>
        )}
        
        {!hasMore && !isLoading && (
          <div className="flex items-center justify-center p-4 border-t bg-background">
            <span className="text-sm text-muted-foreground">No more items to load</span>
          </div>
        )}
      </div>
    </div>
  );
};
